/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:52:59]
Permissions to update outside the main branch: [false]
*/

import { Button, requiredRule } from '@finbeat/react-toolkit';
import { faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BOValidatedDatePicker } from 'base-components/BOValidatedDatePicker/BOValidatedDatePicker';
import { datetimeWithSecondsFormat } from 'constans/dateFormat';
import { addDays, parseISO } from 'date-fns';
import { t } from 'i18next';
import { LeadgenInfo, LeadgenTarificationParameterInfo } from 'models/api/leadgen';
import { RefObject } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import LeadgenModalTarificationRanges from './LeadgenModalTarificationRanges';

const emptyItem: LeadgenTarificationParameterInfo = {
    id: null,
    sd: null,
    ed: null,
    isDeleted: false,
    tarificationRanges: [],
};

type LeadgenModalTarificationParametersProps = {
    disabled: boolean;
    modalRef?: RefObject<HTMLElement>;
};

const LeadgenModalTarificationParameters = ({
    disabled,
    modalRef,
}: LeadgenModalTarificationParametersProps) => {
    const { setValue, getValues, watch } = useFormContext<LeadgenInfo>();

    const addTarificationParameter = () => {
        const tarificationParams = getValues().tarificationParameters;
        let sd: Date;
        if (tarificationParams.length > 0) {
            const tp = tarificationParams.filter((tp) => !tp.isDeleted);
            if (tp.length > 0) {
                sd = tp
                    .map((tp) => (tp.ed ? parseISO(tp.ed) : new Date()))
                    .reduce((prev, curr) => (prev > curr ? prev : curr));
                sd = addDays(sd, 1);
            } else {
                sd = new Date();
            }
        } else {
            sd = new Date();
        }

        const newItem = emptyItem;
        newItem.sd = getStartOfTheDay(sd);
        newItem.ed = getEndOfTheDay(sd);

        setValue('tarificationParameters', [...tarificationParams, newItem]);
    };

    const deleteTarificationParameter = (id: number | null, ids: number) => {
        setValue(
            'tarificationParameters',
            getValues().tarificationParameters.map((x, i) =>
                i === ids ? { ...x, isDeleted: true } : x
            )
        );
    };

    const tarificationParameters = watch('tarificationParameters');

    return (
        <>
            {tarificationParameters && tarificationParameters.length ? (
                tarificationParameters.map((item, ids) => {
                    const check =
                        (item.ed && parseISO(item.ed) < new Date() && item.id != null) || disabled;
                    return (
                        !item.isDeleted && (
                            <Card className="mb-3" key={ids}>
                                <CardHeader className="leadgen-modal__card-header">
                                    <>
                                        {item.id ? `#${item.id}` : t('BackOfficeNew')}
                                        <Button
                                            color="danger"
                                            onClick={() =>
                                                deleteTarificationParameter(item.id, ids)
                                            }
                                            disabled={check}
                                        >
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </Button>
                                    </>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col>
                                            <BOValidatedDatePicker
                                                name={`tarificationParameters[${ids}].sd`}
                                                inputProps={{
                                                    label: t('BackOfficeDtFrom'),
                                                }}
                                                dateFormat={datetimeWithSecondsFormat}
                                                rules={{
                                                    required: requiredRule,
                                                }}
                                                disabled={check}
                                                container={modalRef}
                                            />
                                        </Col>
                                        <Col>
                                            <BOValidatedDatePicker
                                                name={`tarificationParameters[${ids}].ed`}
                                                inputProps={{
                                                    label: t('BackOfficeTo'),
                                                }}
                                                dateFormat={datetimeWithSecondsFormat}
                                                rules={{
                                                    required: requiredRule,
                                                }}
                                                disabled={check}
                                                container={modalRef}
                                            />
                                        </Col>
                                    </Row>

                                    <LeadgenModalTarificationRanges
                                        tarificationParameterId={ids}
                                        disabled={check}
                                    />
                                </CardBody>
                            </Card>
                        )
                    );
                })
            ) : (
                <></>
            )}
            <Button color="secondary" onClick={addTarificationParameter} disabled={disabled}>
                <FontAwesomeIcon icon={faPlus} /> <Trans i18nKey="BackOfficeAdd" />
            </Button>
        </>
    );
};

export default LeadgenModalTarificationParameters;
